import React from 'react';

import { cn } from '@nfctron/nexus-react';

import { Alert } from '@mantine/core';

import { useCountdown } from '@root/lib/hooks/useCountdown';
import { type Component, ComponentDisplayName } from '@root/lib/types/components';

import { type CountdownAlertProps } from './CountdownAlert.types';

/**
 * CountdownAlert component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const CountdownAlert: Component<CountdownAlertProps> = ({ date, onExpire, children, className, ...props }) => {
	const { isFinished, minutes, seconds, hours } = useCountdown(date);
	const isSoonFinishing = hours === 0 && minutes === 0 && seconds <= 30;

	/** call `onExpire` callback upon countdown finishing */
	React.useEffect(() => {
		if (isFinished) onExpire?.();
	}, [isFinished, onExpire]);

	return (
		<Alert color={isSoonFinishing ? 'red' : 'purple-heart'} radius="lg" className={cn([isSoonFinishing && 'animate-pulse'], className)} {...props}>
			<div className={cn(['text-purple-heart-500 flex items-center justify-center gap-1', (isSoonFinishing || isFinished) && 'text-red-500'])}>
				{children(
					`${hours > 0 ? `${hours.toString().padStart(2, '0')}h ` : ''}${minutes > 0 ? `${minutes.toString().padStart(2, '0')}m ` : ''}${seconds
						.toString()
						.padStart(2, '0')}s`,
				)}
			</div>
		</Alert>
	);
};

/**
 * CountdownAlert component display name
 * @type {ComponentDisplayName}
 */
CountdownAlert.displayName = 'component/CountdownAlert';
